// src/pages/admin/AdminFaqs.jsx
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  getFaqs,
  addFaq,
  updateFaq,
  deleteFaq,
  getFaqIllustration,
  updateFaqIllustration,
} from "../../services/firestoreService";
import Modal from "../../components/common/Modal";
import ImageUploader from "../../components/common/ImageUploader";
import Spinner from "../../components/common/Spinner";
import toast from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiHelpCircle, FiArrowUp, FiArrowDown, FiImage } from "react-icons/fi";

export default function AdminFaqs() {
  const [faqs,      setFaqs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [reordering, setReordering] = useState(false);

  // ── Right-side illustration card (image + title + subtitle + badges) ──
  const [illustration,     setIllustration]     = useState(null);
  const [illoLoading,      setIlloLoading]      = useState(true);
  const [illoImage,        setIlloImage]        = useState("");
  const [illoSaving,       setIlloSaving]       = useState(false);
  const {
    register: registerIllo,
    handleSubmit: handleSubmitIllo,
    reset: resetIllo,
  } = useForm();

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const load = async () => {
    const f = await getFaqs();
    setFaqs(f);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const loadIllustration = async () => {
    const data = await getFaqIllustration();
    setIllustration(data);
    setIlloImage(data?.image || "");
    resetIllo({
      title: data?.title || "Tripura Agro",
      subtitle: data?.subtitle || "Pure · Natural · Trusted",
      badge1: data?.badges?.[0] || "🌿 Organic",
      badge2: data?.badges?.[1] || "🚜 Farm Fresh",
      badge3: data?.badges?.[2] || "✅ Quality",
    });
    setIlloLoading(false);
  };
  useEffect(() => { loadIllustration(); }, []);

  const onSubmitIllo = async (data) => {
    setIlloSaving(true);
    try {
      await updateFaqIllustration({
        image: illoImage,
        title: data.title,
        subtitle: data.subtitle,
        badges: [data.badge1, data.badge2, data.badge3].filter(Boolean),
      });
      toast.success("Illustration card updated");
      loadIllustration();
    } catch {
      toast.error("Save failed");
    } finally {
      setIlloSaving(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    reset({ question: "", answer: "" });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    reset({
      question: item.question || "",
      answer: item.answer || "",
    });
    setModalOpen(true);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      if (editing) {
        await updateFaq(editing.id, data);
        toast.success("FAQ updated");
      } else {
        // new FAQs go to the bottom of the list
        const nextOrder = faqs.length
          ? Math.max(...faqs.map((f) => f.order ?? 0)) + 1
          : 0;
        await addFaq({ ...data, order: nextOrder });
        toast.success("FAQ added");
      }
      setModalOpen(false);
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, question) => {
    if (!window.confirm(`Delete FAQ "${question}"?`)) return;
    await deleteFaq(id);
    toast.success("FAQ deleted");
    setFaqs((prev) => prev.filter((f) => f.id !== id));
  };

  // Swap the `order` value of two adjacent FAQs and persist both
  const move = async (index, direction) => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= faqs.length) return;

    setReordering(true);
    try {
      const current = faqs[index];
      const target  = faqs[targetIndex];
      const currentOrder = current.order ?? index;
      const targetOrder  = target.order ?? targetIndex;

      await Promise.all([
        updateFaq(current.id, { order: targetOrder }),
        updateFaq(target.id, { order: currentOrder }),
      ]);

      const next = [...faqs];
      next[index] = { ...target, order: currentOrder };
      next[targetIndex] = { ...current, order: targetOrder };
      setFaqs(next);
    } catch {
      toast.error("Reorder failed");
    } finally {
      setReordering(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">FAQ's</h1>
        <button onClick={openAdd} className="btn-primary flex text-sm sm:text-md items-center gap-2">
          <FiPlus className="w-4 h-4" /> New FAQ
        </button>
      </div>

      {/* ── Right-side illustration card (shown next to FAQs on Home) ── — not needed for now, commented out
      <div className="card p-5 mb-6">

        {illoLoading ? <Spinner center /> : (
          <form onSubmit={handleSubmitIllo(onSubmitIllo)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Image</label>
                <ImageUploader
                  maxImages={1}
                  onUpload={(imgs) => setIlloImage(imgs[0] || "")}
                  existingImages={illoImage ? [illoImage] : []}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Leave empty to show the default 🌾 badge instead of an image.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="label">Title</label>
                  <input {...registerIllo("title")} className="input" placeholder="Tripura Agro" />
                </div>
                <div>
                  <label className="label">Subtitle</label>
                  <input {...registerIllo("subtitle")} className="input" placeholder="Pure · Natural · Trusted" />
                </div>
              </div>
            </div>

            <div>
              <label className="label">Badges (up to 3)</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input {...registerIllo("badge1")} className="input" placeholder="🌿 Organic" />
                <input {...registerIllo("badge2")} className="input" placeholder="🚜 Farm Fresh" />
                <input {...registerIllo("badge3")} className="input" placeholder="✅ Quality" />
              </div>
            </div>

            <button type="submit" disabled={illoSaving} className="btn-primary flex items-center gap-2">
              {illoSaving ? <><Spinner size="sm" /> Saving...</> : "Save Illustration Card"}
            </button>
          </form>
        )}
      </div>
      */}

      {loading ? <Spinner center /> : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {["Order", "Question", "Answer", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {faqs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-12 text-gray-500">
                    No FAQs yet. Add your first one!
                  </td>
                </tr>
              ) : (
                faqs.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          disabled={reordering || index === 0}
                          onClick={() => move(index, "up")}
                          className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <FiArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          disabled={reordering || index === faqs.length - 1}
                          onClick={() => move(index, "down")}
                          className="p-1 rounded hover:bg-gray-100 text-gray-500 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <FiArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 max-w-md">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 flex-shrink-0">
                          <FiHelpCircle className="w-4 h-4" />
                        </div>
                        <span className="font-medium text-gray-900">{item.question}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-md">
                      <p className="truncate">{item.answer}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:bg-primary-50 text-primary-600 transition-colors">
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(item.id, item.question)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 transition-colors">
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editing ? "Edit FAQ" : "New FAQ"}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="label">Question *</label>
            <input
              {...register("question", { required: "Question is required" })}
              className={`input ${errors.question ? "border-red-400" : ""}`}
              placeholder="e.g. Are your spices 100% natural?"
            />
            {errors.question && <p className="text-xs text-red-500 mt-1">{errors.question.message}</p>}
          </div>

          <div>
            <label className="label">Answer *</label>
            <textarea
              {...register("answer", { required: "Answer is required" })}
              rows={5}
              className={`input resize-none ${errors.answer ? "border-red-400" : ""}`}
              placeholder="Write the answer shown to customers"
            />
            {errors.answer && <p className="text-xs text-red-500 mt-1">{errors.answer.message}</p>}
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary flex-1 flex items-center justify-center gap-2">
              {saving ? <><Spinner size="sm" /> Saving...</> : editing ? "Update FAQ" : "Add FAQ"}
            </button>
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}